import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IMAGE_CROP_POLL_INTERVAL_MS } from '../constants/imageCropConfig';
import { pollImageCropTaskOnce } from '../utils/imageCropPoll';
import {
  isImageCropFatalPollError,
  presentImageCropPollError,
} from '../utils/imageCropTaskApiError';
import {
  isDocumentVisible,
  resolveBackgroundPollIntervalMs,
  subscribePageVisibility,
} from '@/utils/pageVisibility';

const EMPTY_IMAGE_IDS = [];

export function createIdleImageCropProgress() {
  return {
    active: false,
    rows: [],
    imageIds: [],
    total: 0,
    successCount: 0,
    failedCount: 0,
    pendingCount: 0,
    status: 'processing',
    detectBatches: 0,
    detectBatchIndex: 0,
    done: false,
  };
}

const IDLE_IMAGE_CROP_PROGRESS = createIdleImageCropProgress();

/**
 * T3-3.3：React 轮询 hook，Studio / Detail 共用 Worker 进度拉取。
 *
 * 致命错误（404/400/410）立即停表，避免详情页失败任务疯狂打 batch 接口。
 *
 * @param {object} options
 * @param {string} [options.taskId]
 * @param {string[]} [options.imageIds]
 * @param {boolean} [options.enabled]
 * @param {number} [options.intervalMs]
 * @param {number} [options.detectBatches]
 * @param {() => boolean} [options.shouldAbort]
 * @param {(snapshot: object) => void | Promise<void>} [options.onProgress]
 * @param {(error: Error) => void | Promise<void>} [options.onPollError]
 * @param {(message: string, type?: string) => void} [options.showToast]
 * @param {boolean} [options.silentPollErrors] 轮询失败不弹 toast（详情页推荐）
 */
export function useImageCropProgress({
  taskId,
  imageIds = EMPTY_IMAGE_IDS,
  enabled = false,
  intervalMs = IMAGE_CROP_POLL_INTERVAL_MS,
  detectBatches,
  shouldAbort,
  onProgress,
  onPollError,
  showToast,
  silentPollErrors = false,
} = {}) {
  const [progress, setProgress] = useState(IDLE_IMAGE_CROP_PROGRESS);
  const [polling, setPolling] = useState(false);
  const [pollError, setPollError] = useState(null);
  const inFlightRef = useRef(false);
  const pollErrorNotifiedRef = useRef(false);
  const stoppedFatalRef = useRef(false);
  const onProgressRef = useRef(onProgress);
  const onPollErrorRef = useRef(onPollError);
  const showToastRef = useRef(showToast);
  const shouldAbortRef = useRef(shouldAbort);
  const detectBatchesRef = useRef(detectBatches);
  const imageIdsRef = useRef(imageIds);
  const silentPollErrorsRef = useRef(silentPollErrors);

  onProgressRef.current = onProgress;
  onPollErrorRef.current = onPollError;
  showToastRef.current = showToast;
  shouldAbortRef.current = shouldAbort;
  detectBatchesRef.current = detectBatches;
  imageIdsRef.current = imageIds;
  silentPollErrorsRef.current = silentPollErrors;

  const imageIdsKey = useMemo(
    () => (imageIds || EMPTY_IMAGE_IDS).map(String).join('|'),
    [imageIds],
  );

  const applySnapshot = useCallback(async (snapshot) => {
    if (!snapshot) return null;
    pollErrorNotifiedRef.current = false;
    stoppedFatalRef.current = false;
    setPollError(null);
    const next = {
      active: true,
      ...snapshot,
    };
    setProgress(next);
    await onProgressRef.current?.(next);
    return next;
  }, []);

  const notifyPollError = useCallback(async (error) => {
    const fatal = isImageCropFatalPollError(error);
    const shouldNotify = !pollErrorNotifiedRef.current;
    pollErrorNotifiedRef.current = true;
    if (fatal) {
      stoppedFatalRef.current = true;
    }

    const presented = shouldNotify
      ? presentImageCropPollError(error, {
        showToast: silentPollErrorsRef.current ? undefined : showToastRef.current,
        silent: silentPollErrorsRef.current || fatal,
      })
      : (error instanceof Error ? error : new Error(String(error ?? '进度刷新失败')));

    presented.pollError = true;
    presented.pollFatal = fatal;
    setPollError(presented);
    if (shouldNotify) {
      console.warn('[useImageCropProgress] poll failed:', presented);
      await onPollErrorRef.current?.(presented);
    }
    return presented;
  }, []);

  const refresh = useCallback(async () => {
    const tid = String(taskId || '').trim();
    if (!tid || inFlightRef.current) return null;
    if (stoppedFatalRef.current) return null;
    if (shouldAbortRef.current?.()) return null;

    inFlightRef.current = true;
    setPolling(true);
    try {
      const snapshot = await pollImageCropTaskOnce(tid, imageIdsRef.current, {
        shouldAbort: shouldAbortRef.current,
        detectBatches: detectBatchesRef.current,
      });
      return await applySnapshot(snapshot);
    } catch (error) {
      return await notifyPollError(error);
    } finally {
      inFlightRef.current = false;
      setPolling(false);
    }
  }, [taskId, applySnapshot, notifyPollError]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    const tid = String(taskId || '').trim();
    if (!enabled || !tid) {
      pollErrorNotifiedRef.current = false;
      stoppedFatalRef.current = false;
      setPollError(null);
      setProgress((prev) => (prev.active ? IDLE_IMAGE_CROP_PROGRESS : prev));
      return undefined;
    }

    let cancelled = false;
    const safeInterval = Math.max(500, Number(intervalMs) || IMAGE_CROP_POLL_INTERVAL_MS);
    const hiddenInterval = resolveBackgroundPollIntervalMs(safeInterval, Math.max(safeInterval * 3, 10_000));
    let timerId = null;

    const stopTimer = () => {
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
    };

    const runTick = async () => {
      if (cancelled || stoppedFatalRef.current) {
        stopTimer();
        return;
      }
      const result = await refreshRef.current();
      if (cancelled) return;
      if (stoppedFatalRef.current || result?.pollFatal || result?.done) {
        stopTimer();
      }
    };

    const schedule = () => {
      stopTimer();
      if (cancelled || stoppedFatalRef.current) return;
      const hidden = !isDocumentVisible();
      if (hidden && hiddenInterval == null) return;
      const delay = hidden ? hiddenInterval : safeInterval;
      timerId = setTimeout(async () => {
        await runTick();
        if (!cancelled && !stoppedFatalRef.current) schedule();
      }, delay);
    };

    const onVisibility = (visible) => {
      if (cancelled) return;
      if (visible) {
        void runTick().then(() => {
          if (!cancelled && !stoppedFatalRef.current) schedule();
        });
        return;
      }
      schedule();
    };

    void runTick().then(() => {
      if (!cancelled && !stoppedFatalRef.current) schedule();
    });
    const unsubscribe = subscribePageVisibility(onVisibility);

    return () => {
      cancelled = true;
      stopTimer();
      unsubscribe();
    };
  }, [enabled, taskId, intervalMs, imageIdsKey]);

  return {
    progress,
    polling,
    pollError,
    refresh,
  };
}

export default useImageCropProgress;
